import { unstable_cache } from "next/cache";
import { type Blog } from "@/lib/@types/api/blog";
import { defaultLocale } from "@/config";

// 博客列表缓存时间（秒），减少重复请求
const BLOG_LIST_CACHE_SECONDS = 300;
const BLOG_DETAIL_CACHE_SECONDS = 600;


// 获取博客分类
export const getBlogCls=async (params: Blog.GetBlogClsParams): Promise<Blog.BlogCl[]> => {
    if(!params.lang_code.lang_code)params.lang_code.lang_code=defaultLocale
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/cls_filter`;
    const res = (await fetch(url, {
        method: "POST",
        body: JSON.stringify({
            ...params,
        }),
        headers: {
            "Content-Type": "application/json"
        },
        next:{ revalidate: 60000 }
    }).then((r) => r.json())) as Blog.GetBlogClsResp;
    if (res.code === 200) {
        return res.detail.cls_list;
    } else {
        throw new Error(res.message);
    }
};
// 获取博标签
export const getBlogTags=async (params: Blog.GetBlogClsParams): Promise<Blog.BlogTag[]> => {
    if(!params.lang_code.lang_code)params.lang_code.lang_code=defaultLocale

    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/tag/${params.lang_code.lang_code}`;
    const res = (await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        next:{ revalidate: 60000 }
    }).then((r) => r.json())) as Blog.GetBlogTagResp;
    if (res.code === 200) {
        return res.detail.tag_list;
    } else {
        throw new Error(res.message);
    }
};

// 获取博客列表（内部实现，供缓存包装调用）
async function getBlogListFetch(params: Blog.GetBlogListParams): Promise<Blog.GetBlogListResp> {
    if (!params.lang_code.lang_code) params.lang_code.lang_code = defaultLocale;
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/blog_filter`;
    const res = (await fetch(url, {
        method: "POST",
        body: JSON.stringify({ ...params }),
        headers: { "Content-Type": "application/json" },
        next: { revalidate: BLOG_LIST_CACHE_SECONDS },
    }).then((r) => r.json())) as Blog.GetBlogListResp;
    if (res.code === 200) return res;
    throw new Error(res.message);
}

// 获取博客列表。该方法也会在客户端组件中调用，因此不能使用仅服务端可用的 unstable_cache。
// 服务端请求仍由 getBlogListFetch 中 fetch 的 next.revalidate 负责缓存。
export const getBlogList = async (params: Blog.GetBlogListParams): Promise<Blog.GetBlogListResp> => {
    return getBlogListFetch(params);
};

/**
 * 单次请求博客详情（不缓存）。先读 text 再 JSON.parse，避免上游返回 HTML/纯文本时 r.json() 抛错。
 * lang_code、slug 做 encode，避免异常字符破坏 URL；当前语言无稿时由外层回退 defaultLocale。
 */
async function fetchBlogDetailOnce(slug: string, locale: string): Promise<Blog.BlogContent | null> {
    const base = process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL;
    if (!base) {
        console.error("getBlogDetail: NEXT_PUBLIC_LANGUAGE_LIST_URL is not set");
        return null;
    }
    const pathSlug = encodeURIComponent(slug);
    const lang = encodeURIComponent(locale);
    const url = `${base}/blog/blog_slug/${pathSlug}?lang_code=${lang}`;
    const response = await fetch(url, {
        headers: { Accept: "application/json" },
        next: { revalidate: BLOG_DETAIL_CACHE_SECONDS },
    });
    const text = await response.text();
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        console.error("getBlogDetail: non-JSON response", {
            status: response.status,
            url,
            snippet: text.slice(0, 200),
        });
        return null;
    }
    const res = parsed as Blog.GetBlogDetailResp;
    if (res.code === 200 && res.detail?.blog_content) {
        return res.detail.blog_content;
    }
    return null;
}

// 获取博客详情（带 cache；缓存 key 含版本号以便在修复解析逻辑后丢弃旧的错误 null 缓存）
export const getBlogDetail = async (slug: string, locale: string): Promise<Blog.BlogContent | null> => {
    const cacheKey = ["blog-detail", "v2", slug, locale];
    return unstable_cache(
        async () => {
            let blog = await fetchBlogDetailOnce(slug, locale);
            if (!blog && locale !== defaultLocale) {
                blog = await fetchBlogDetailOnce(slug, defaultLocale);
            }
            return blog;
        },
        cacheKey,
        { revalidate: BLOG_DETAIL_CACHE_SECONDS }
    )();
};


export const getBlogSlug = async (params: {page:number,amount:number}): Promise<Blog.getBlogSlugResp> => {
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/get_blog_slug?page=${params.page}&amount=${params.amount}`;
    const res = (await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        next:{ revalidate: 600 }
    }).then((r) => r.json())) as Blog.getBlogSlugResp;
    if (res.code === 200) {
        return res
    } else {
        throw new Error(res.message);
    }
};

export  const getBlogComment=async (page:number,limit:number,blog_id:number)=>{
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/comment?page=${page}&limit=${limit}&blog_id=${blog_id}`;
    const res = (await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        next:{ revalidate: 600 }
    }).then((r) => r.json())) as Blog.GetBlogCommentResp;
    if (res.code === 200) {
        return res
    } else {
        throw new Error(res.message);
    }
}

export const SubmitBlogComment=async (data:Blog.BlogCommentItem)=>{
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/comment`;
    const res = (await fetch(url, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
            "Content-Type": "application/json"
        },
        next:{ revalidate: 600 }
    }).then((r) => r.json())) as Blog.BaseResp<any>;
    if (res.code === 200) {
        return res
    } else {
        throw new Error(res.message);
    }
}

async function getBlogAuthorFetch(author_username: string) {
    const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/user/author?author_username=${author_username}`;
    const res = (await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        next: { revalidate: 600 },
    }).then((r) => r.json())) as Blog.BlogAuthorResp;
    if (res.code === 200) return res.detail.author_detail;
    throw new Error(res.message);
}

/** 获取博客作者（带缓存，同一作者多次请求命中缓存） */
export const GetBlogAuthor = async (author_username: string) => {
    if (!author_username) return;
    return unstable_cache(
        () => getBlogAuthorFetch(author_username),
        ["blog-author", author_username],
        { revalidate: 600 }
    )();
};
